import { Injectable, inject } from '@angular/core';
import { QuestionService } from '_services/question.service';
import { UserService } from '_services/user.service';
import { UnivPrefsService } from '_services/univ-prefs.service';
import {
  IncompleteSection,
  JourneyNavState,
  JourneyNavView,
  JourneyStage,
  JourneySubstep,
  StageIcon,
  StageId,
  StageStatus,
  StageStripItem,
} from '_services/journey';

const STAGE_LABELS: Record<StageId, string> = {
  student: 'Student',
  household: 'Household',
  finances: 'Finances',
  results: 'Results',
};

const STAGE_ICONS: Record<StageId, StageIcon> = {
  student: 'student',
  household: 'household',
  finances: 'finances',
  results: 'results',
};

const FINANCE_COMPACT_LABELS: Record<string, string> = {
  PI: 'Income',
  PA: 'Assets',
  SF: 'Student',
};

@Injectable()
export class JourneyService {
  private userService = inject(UserService);
  private univPrefs = inject(UnivPrefsService);
  private questionService = inject(QuestionService);

  absolutePath(route: string): string {
    return `/app/${this.userService.schoolId}/${route}`;
  }

  /** Relative back/next for show-form (../segment). Empty back when none. */
  relativeRoutesForSection(sectionId: string): { backRoute: string; nextRoute: string } {
    const ordered = this.buildOrderedSubsteps();
    const index = ordered.findIndex(s => s.sectionId === sectionId);
    if (index < 0) {
      return { backRoute: '', nextRoute: '../results' };
    }
    const back = index > 0 ? ordered[index - 1] : null;
    const next = index < ordered.length - 1 ? ordered[index + 1] : null;
    return {
      backRoute: back ? `../${back.route}` : '',
      nextRoute: next ? `../${next.route}` : '../results',
    };
  }

  getNavView(currentRouteSegment: string | null): JourneyNavView {
    const state = this.getNavState(currentRouteSegment);
    return {
      ...state,
      stripItems: this.buildStripItems(state),
      financeSubItems: this.buildFinanceSubItems(state),
      statusAnnouncement: this.buildStatusAnnouncement(state),
    };
  }

  getNavState(currentRouteSegment: string | null): JourneyNavState {
    const orderedSubsteps = this.buildOrderedSubsteps();
    const stages = this.groupIntoStages(orderedSubsteps);
    const currentSubstep = orderedSubsteps.find(s => s.route === currentRouteSegment) ?? null;
    const currentStageId = this.resolveCurrentStageId(currentSubstep);

    return {
      stages,
      orderedSubsteps,
      currentSubstep,
      currentStageId,
      stageStatuses: this.computeStageStatuses(stages, currentStageId),
    };
  }

  isSubstepComplete(substep: JourneySubstep): boolean {
    if (substep.id === 'results') {
      return this.userService.progress === 100 || this.userService.isDemo;
    }
    if (substep.id === 'dependency') {
      const gatewayOk = this.questionService.isDependencyResolved();
      // NPC school questions live on the dependency page; Aid Quiz keeps a separate SI step.
      const schoolOk = this.userService.isAidQuiz
        || !this.univPrefs.hasCustomQuestions
        || this.questionService.invalidByCategory('SI') === '';
      return gatewayOk && schoolOk;
    }
    if (substep.sectionId) {
      return this.questionService.invalidByCategory(substep.sectionId) === '';
    }
    return false;
  }

  /** All calculator stages are navigable from the stage strip. */
  isSubstepReachable(_substep: JourneySubstep, _ordered?: JourneySubstep[]): boolean {
    return true;
  }

  /** Sections still missing answers — used by Results incomplete banner. */
  incompleteSections(): IncompleteSection[] {
    const ordered = this.buildOrderedSubsteps();
    const unanswered = this.questionService.unansweredVisibleQuestions();
    const claimedIds = new Set<string>();
    const sections: IncompleteSection[] = [];

    for (const step of ordered) {
      if (step.id === 'results') {
        continue;
      }

      const questions = this.unansweredForSubstep(step, unanswered)
        .filter(q => !claimedIds.has(q.id));
      for (const q of questions) {
        claimedIds.add(q.id);
      }

      const gatewayMissing = step.id === 'dependency' && !this.questionService.isDependencyResolved();
      if (!questions.length && !gatewayMissing) {
        continue;
      }

      if (gatewayMissing) {
        questions.unshift({
          id: 'student.compoundIndependent',
          label: 'Whose financial information will you enter?',
        });
        claimedIds.add('student.compoundIndependent');
      }

      sections.push({
        label: step.label,
        route: step.route,
        count: questions.length || 1,
        stageId: step.stageId,
        questions,
      });
    }

    const orphans = unanswered.filter(q => !claimedIds.has(q.id));
    if (orphans.length) {
      sections.push({
        label: 'Other unanswered questions',
        route: this.userService.isAidQuiz ? 'household-information' : 'dependency',
        count: orphans.length,
        stageId: 'student',
        questions: orphans.map(({ id, label }) => ({ id, label })),
      });
    }

    return sections;
  }

  private unansweredForSubstep(
    step: JourneySubstep,
    unanswered: Array<{ id: string; label: string; sectionId: string }>,
  ): Array<{ id: string; label: string }> {
    const sectionIds = new Set<string>();
    if (step.sectionId) {
      sectionIds.add(step.sectionId);
    }
    if (step.id === 'dependency' && !this.userService.isAidQuiz && this.univPrefs.hasCustomQuestions) {
      sectionIds.add('SI');
    }
    return unanswered
      .filter(q => sectionIds.has(q.sectionId))
      .map(({ id, label }) => ({ id, label }));
  }

  buildOrderedSubsteps(): JourneySubstep[] {
    const steps: JourneySubstep[] = [];
    const shortName = this.univPrefs.preferences()?.shortName;
    const schoolLabel = shortName ? `${shortName} questions` : 'School questions';

    if (!this.userService.isAidQuiz) {
      steps.push({
        id: 'dependency',
        route: 'dependency',
        label: 'Student',
        stageId: 'student',
      });
    }

    steps.push({
      id: 'household-information',
      route: 'household-information',
      sectionId: 'HP',
      label: this.userService.isDependent ? "Parents' household" : "Student's household",
      stageId: 'household',
    });

    if (this.userService.isDependent) {
      steps.push({
        id: 'parent-income',
        route: 'parent-income',
        sectionId: 'PI',
        label: 'Parent income',
        stageId: 'finances',
      });
      steps.push({
        id: 'parent-assets',
        route: 'parent-assets',
        sectionId: 'PA',
        label: 'Parent assets',
        stageId: 'finances',
      });
    }

    steps.push({
      id: 'student-finances',
      route: 'student-finances',
      sectionId: 'SF',
      label: 'Student finances',
      stageId: 'finances',
    });

    // Aid Quiz has no dependency page; keep school questions before results there.
    if (this.univPrefs.hasCustomQuestions && this.userService.isAidQuiz) {
      steps.push({
        id: 'student-information',
        route: 'student-information',
        sectionId: 'SI',
        label: schoolLabel,
        stageId: 'student',
      });
    }

    steps.push({
      id: 'results',
      route: this.userService.isAidQuiz ? 'sai-results' : 'results',
      label: 'Results',
      stageId: 'results',
    });

    return steps;
  }

  /** Always four stage cards; finances never expands into the main strip. */
  private buildStripItems(state: JourneyNavState): StageStripItem[] {
    return state.stages.map((stage, stageIndex) => {
      const status = state.stageStatuses[stage.id] ?? 'incomplete';
      const target = this.stageNavTarget(stage, state);
      const incompleteCount = this.incompleteCountForStage(stage);
      const subtitle = this.stageSubtitle(stage, status, incompleteCount);
      return {
        id: stage.id,
        label: stage.label,
        displayNumber: String(stageIndex + 1),
        status,
        route: target.route,
        subtitle,
        icon: STAGE_ICONS[stage.id],
        incompleteCount: incompleteCount || undefined,
        ariaLabel: this.stepAriaLabel(String(stageIndex + 1), stage.label, status, subtitle),
      };
    });
  }

  private buildFinanceSubItems(state: JourneyNavState): StageStripItem[] {
    const finance = state.stages.find(s => s.id === 'finances');
    if (state.currentStageId !== 'finances' || !finance || finance.substeps.length <= 1) {
      return [];
    }
    return finance.substeps.map((step, subIndex) => {
      const isCurrent = state.currentSubstep?.id === step.id;
      const complete = this.isSubstepComplete(step);
      const status: StageStatus = isCurrent ? 'current' : complete ? 'complete' : 'incomplete';
      const count = this.incompleteCountForSubstep(step);
      const label = step.sectionId ? (FINANCE_COMPACT_LABELS[step.sectionId] ?? step.label) : step.label;
      const subtitle = this.substepSubtitle(status, count);
      return {
        id: step.id,
        label,
        displayNumber: String(subIndex + 1),
        status,
        route: step.route,
        subtitle,
        icon: 'finances' as StageIcon,
        incompleteCount: count || undefined,
        ariaLabel: this.stepAriaLabel(String(subIndex + 1), label, status, subtitle),
      };
    });
  }

  /** First incomplete substep in the stage, else first; when on finances prefer current. */
  private stageNavTarget(stage: JourneyStage, state: JourneyNavState): JourneySubstep {
    if (stage.id === 'finances' && state.currentStageId === 'finances' && state.currentSubstep) {
      const current = state.currentSubstep;
      if (stage.substeps.some(s => s.id === current.id)) {
        return current;
      }
    }
    return stage.substeps.find(s => !this.isSubstepComplete(s)) ?? stage.substeps[0];
  }

  private incompleteCountForStage(stage: JourneyStage): number {
    if (stage.id === 'results') {
      return this.isSubstepComplete(stage.substeps[0]) ? 0 : 1;
    }
    return stage.substeps.reduce((sum, s) => sum + this.incompleteCountForSubstep(s), 0);
  }

  private incompleteCountForSubstep(step: JourneySubstep): number {
    if (this.isSubstepComplete(step)) {
      return 0;
    }
    if (step.id === 'dependency') {
      let count = this.questionService.isDependencyResolved() ? 0 : 1;
      if (!this.userService.isAidQuiz && this.univPrefs.hasCustomQuestions) {
        count += Number(this.questionService.invalidByCategory('SI') || 0);
      }
      return Math.max(count, 1);
    }
    if (step.sectionId) {
      return Math.max(Number(this.questionService.invalidByCategory(step.sectionId) || 0), 1);
    }
    return 1;
  }

  private stageSubtitle(stage: JourneyStage, status: StageStatus, incompleteCount: number): string {
    if (status === 'current') {
      return 'Current';
    }
    if (stage.id === 'results') {
      return status === 'complete' ? 'Ready' : 'Preview';
    }
    if (status === 'complete') {
      return 'Complete';
    }
    if (incompleteCount > 0) {
      return incompleteCount === 1 ? '1 question left' : `${incompleteCount} questions left`;
    }
    return 'Incomplete';
  }

  private substepSubtitle(status: StageStatus, count: number): string {
    if (status === 'current') {
      return 'Current';
    }
    if (status === 'complete') {
      return 'Complete';
    }
    if (count > 0) {
      return count === 1 ? '1 left' : `${count} left`;
    }
    return 'Incomplete';
  }

  private groupIntoStages(ordered: JourneySubstep[]): JourneyStage[] {
    const stages: JourneyStage[] = [];
    for (const id of ['student', 'household', 'finances', 'results'] as StageId[]) {
      const substeps = ordered.filter(s =>
        id === 'student'
          ? s.stageId === 'student' && s.sectionId !== 'SI'
          : s.stageId === id
      );
      if (substeps.length) {
        stages.push({ id, label: STAGE_LABELS[id], substeps });
      }
    }
    return stages;
  }

  private resolveCurrentStageId(current: JourneySubstep | null): StageId | null {
    if (!current || current.sectionId === 'SI') {
      return null;
    }
    return current.stageId;
  }

  /**
   * Stage completion is data-driven (section validity / progress), not route order.
   * The current stage is always shown as current even if its answers are already valid.
   */
  private computeStageStatuses(
    stages: JourneyStage[],
    currentStageId: StageId | null,
  ): Record<StageId, StageStatus> {
    const statuses: Record<StageId, StageStatus> = {
      student: 'incomplete',
      household: 'incomplete',
      finances: 'incomplete',
      results: 'incomplete',
    };

    for (const stage of stages) {
      if (stage.id === currentStageId) {
        statuses[stage.id] = 'current';
        continue;
      }
      const complete = stage.substeps.every(s => this.isSubstepComplete(s));
      statuses[stage.id] = complete ? 'complete' : 'incomplete';
    }

    if (currentStageId) {
      statuses[currentStageId] = 'current';
    }
    return statuses;
  }

  private stepAriaLabel(
    number: string,
    label: string,
    status: StageStatus,
    subtitle: string,
  ): string {
    return `Step ${number}, ${label}, ${subtitle}`;
  }

  private buildStatusAnnouncement(state: JourneyNavState): string {
    const current = state.currentSubstep;
    if (!current) {
      return '';
    }
    if (current.sectionId === 'SI') {
      return `${current.label}. Complete these questions, then continue to results.`;
    }
    if (state.currentStageId === 'finances') {
      const finance = state.stages.find(s => s.id === 'finances');
      if (finance && finance.substeps.length > 1) {
        return `Finances: ${current.label}`;
      }
    }
    const stage = state.stages.find(s => s.id === current.stageId);
    return stage ? `${stage.label}: ${current.label}` : current.label;
  }
}
